import { ChatbotMessage } from '../pnl-chatbot-container/pnl-chatbot-messages/pnl-chatbot-message/pnl-chatbot-message';
import {
    ChatbotCaseOverview,
    ChatbotDatePicker,
    ChatbotFaq,
    ChatbotMultiSelect,
    ChatbotResponseButton,
    ChatbotResponseOptionButtons,
    ChatbotUploadFileButton
} from '../pnl-chatbot-container/models/chatbot.models';
import { ChatbotLiveSession } from '../pnl-chatbot-container/pnl-chatbot-live-session/pnl-chatbot-live-session';

export class sessionData {
    sessionId: string;
    dialogFlowAgent: string;
    welcomeIntent: string;
    startIntent: string;
    botName: string;
    messages: ChatbotMessage[];
    suggestions: Array<ChatbotResponseButton>;
    multiSelect: ChatbotMultiSelect;
    quickReplies: Array<ChatbotResponseButton>;
    optionButtons: ChatbotResponseOptionButtons;
    chatbotLiveSession: ChatbotLiveSession;
    inactivityTimeout: number;
    chatMinimized: boolean;
    unreadMessages: number;
    liveChatConnectionBeingEstablished: boolean;
    queuePosition: number;
    currentLanguage: string;
    loginFollowupEvent: string;
    notLoginFollowupEvent: string;
    hasBeenRedirectedToLogin: boolean;
    uploadFileButton: string;
    caseOverview: ChatbotCaseOverview;
    showAllQuickReplies: boolean;
    datePicker: ChatbotDatePicker;
    faqs: ChatbotFaq[];
    enableUploadFileLiveAgent: boolean;
    amountOfFilesUploadedLiveAgent: number;
}

export class ChatbotTunnel {
    public sessionDataFound: Promise<sessionData>;
    private _iframe?: HTMLIFrameElement;
    private tunnelOrigin: string;
    private resolveSessionDataFound: (value?: sessionData | PromiseLike<sessionData>) => void;
    private resolveSessionTokenRefreshed: (value?: string | PromiseLike<string>) => void;

    constructor(
        private tunnelPageUrl: string
    ) {
        this._iframe = document.getElementById('chatbot-tunnel') as HTMLIFrameElement;
        this.tunnelOrigin = this.getOrigin(this.tunnelPageUrl);
        if (!this._iframe) {
            this._iframe = window.document.createElement('iframe');
            this._iframe.id = 'chatbot-tunnel';
            this._iframe.src = this.tunnelPageUrl;
            window.document.head!.appendChild(this._iframe);
        }

        this.sessionDataFound = new Promise((resolve) => {
            this.resolveSessionDataFound = resolve;
        });

        window.addEventListener('message', this.receiveMessage.bind(this), false);
    }

    public chatWindowsOpened(agent: string, startIntent: string, welcomeIntent: string, botName: string) {
        this._iframe.addEventListener('load', () => {
            this.postMessageWindowOpened(agent, startIntent, welcomeIntent, botName);
        });
        this.postMessageWindowOpened(agent, startIntent, welcomeIntent, botName);
    }

    public postMessageRetrieveCurrentData() {
        // we need to let the page render the iframe before sending request
        setTimeout(() => {
            this._iframe.contentWindow.postMessage({
                type: 'postnl:chatbot:retrieve-current-data'
            }, this.tunnelOrigin);
        }, 500);
    }

    private postMessageWindowOpened(agent: string, startIntent: string, welcomeIntent: string, botName: string ) {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:chat-window-opened',
            data: {
                startIntent: startIntent,
                welcomeIntent: welcomeIntent,
                dialogFlowAgent: agent,
                botName: botName
            }
        }, this.tunnelOrigin);
    }

    public refreshSessionToken(): Promise<string> {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:refresh-session-token',
        }, this.tunnelOrigin)

        return new Promise<string>((resolve) => this.resolveSessionTokenRefreshed = resolve );
    }

    public updateInactivityTimer(timer: number) {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:inactivity-timer-changed',
            data: timer
        }, this.tunnelOrigin);
    }

    public updateExtraData(
        multiSelect: ChatbotMultiSelect,
        quickReplies: Array<ChatbotResponseButton>,
        suggestions: Array<ChatbotResponseButton>,
        chatbotLiveSession: ChatbotLiveSession,
        liveChatConnectionBeingEstablished: boolean,
        queuePosition: number,
        unreadMessages: number,
        chatMinimized: boolean,
        optionButtons: ChatbotResponseOptionButtons,
        currentLanguage: string,
        loginFollowupEvent: string,
        notLoginFollowupEvent: string,
        hasBeenRedirectedToLogin: boolean,
        uploadFileButton: ChatbotUploadFileButton,
        showAllQuickReplies: boolean,
        caseOverview: ChatbotCaseOverview,
        datePicker: ChatbotDatePicker,
        faqs: ChatbotFaq[],
        enableUploadFileLiveAgent: boolean,
        amountOfFilesUploadedLiveAgent: number) {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:extra-data-update',
            data: {
                multiSelect: multiSelect,
                suggestions: suggestions,
                quickReplies: quickReplies,
                chatbotLiveSession: chatbotLiveSession,
                liveChatConnectionBeingEstablished: liveChatConnectionBeingEstablished,
                queuePosition: queuePosition,
                unreadMessages: unreadMessages,
                chatMinimized: chatMinimized,
                optionButtons: optionButtons,
                currentLanguage: currentLanguage,
                loginFollowupEvent: loginFollowupEvent,
                notLoginFollowupEvent: notLoginFollowupEvent,
                hasBeenRedirectedToLogin: hasBeenRedirectedToLogin,
                uploadFileButton: uploadFileButton,
                caseOverview: caseOverview,
                showAllQuickReplies: showAllQuickReplies,
                datePicker: datePicker,
                faqs: faqs,
                enableUploadFileLiveAgent: enableUploadFileLiveAgent,
                amountOfFilesUploadedLiveAgent: amountOfFilesUploadedLiveAgent
            }
        }, this.tunnelOrigin);
    }

    public newMessagesReceived(messages: Array<ChatbotMessage>) {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:new-chat-messages',
            data: messages
        }, this.tunnelOrigin);
    }

    public clearChat() {
        this._iframe.contentWindow.postMessage({
            type: 'postnl:chatbot:clear-chat',
        }, this.tunnelOrigin);
    }

    private receiveMessage(event: MessageEvent) {
        const origin = event.origin;
        if (origin !== this.tunnelOrigin) {
            return;
        }

        if (!event.data || !event.data.type) {
            return;
        }

        switch (event.data.type) {
            case 'postnl:chatbot:existing-chat-converstion-found':
            case 'postnl:chatbot:chat-conversation-started':
                this.resolveSessionDataFound(event.data.data);
                break;
            case 'postnl:chatbot:session-token-refreshed':
                if(this.resolveSessionTokenRefreshed){
                    this.resolveSessionTokenRefreshed(event.data.data)
                }
                break;
        }
    }

    private getOrigin(url: string) {
        var temp = document.createElement('a');
        temp.href = url;
        return temp.origin;
    }
}
